import { useState } from 'react';

import {
  ArrowRight,
  Calendar,
  Clock,
  GraduationCap,
  HeadphonesIcon,
  Puzzle,
  Rocket,
  Settings,
  Shield,
} from 'lucide-react';
import { motion } from 'motion/react';

import { DiagramContainer } from '@src/components/DiagramContainer';
import AnswerSection from '@src/components/seo/AnswerSection';
import { Button } from '@src/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@src/components/ui/card';
import { useNavigateToTop } from '@src/hooks/useNavigateToTop';

const BOSSS_BLUE = '#1D4ED8';
const BG_COLOR = '#F8FAFC';
const TEXT_COLOR = '#384656';

// Helper function for precise line connections in circular layout
const calculateCircularPosition = (
  centerX,
  centerY,
  radius,
  angle,
) => {
  return {
    x: centerX + radius * Math.cos(angle),
    y: centerY + radius * Math.sin(angle),
  };
};

// Implementation Phases Circular Diagram
function ImplementationPhasesCircularDiagram() {
  const [hoveredPhase, setHoveredPhase] = useState(null);

  const centerX = 180;
  const centerY = 120;
  const radius = 80;

  const phases = [
    {
      label: 'Plan',
      color: '#10B981',
      icon: Calendar,
      angle: -Math.PI / 2,
    },
    {
      label: 'Configure',
      color: '#F59E0B',
      icon: Settings,
      angle: -Math.PI / 2 + (2 * Math.PI) / 5,
    },
    {
      label: 'Integrate',
      color: '#EF4444',
      icon: Puzzle,
      angle: -Math.PI / 2 + (4 * Math.PI) / 5,
    },
    {
      label: 'Train',
      color: '#8B5CF6',
      icon: GraduationCap,
      angle: -Math.PI / 2 + (6 * Math.PI) / 5,
    },
    {
      label: 'Launch',
      color: BOSSS_BLUE,
      icon: Rocket,
      angle: -Math.PI / 2 + (8 * Math.PI) / 5,
    },
  ];

  const phasePositions = phases.map((phase) => ({
    ...phase,
    ...calculateCircularPosition(
      centerX,
      centerY,
      radius,
      phase.angle,
    ),
  }));

  return (
    <DiagramContainer className="w-full max-w-md mx-auto">
      <svg viewBox="0 0 360 240" className="w-full h-auto">
        {/* Central Hub */}
        <motion.circle
          r="35"
          cx={centerX}
          cy={centerY}
          fill={BOSSS_BLUE}
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          style={{
            filter:
              'drop-shadow(0 4px 12px rgba(29, 78, 216, 0.3))',
          }}
        />

        <motion.text
          x={centerX}
          fill="white"
          fontSize="10"
          y={centerY - 5}
          fontWeight="bold"
          textAnchor="middle"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, delay: 0.9 }}
        >
          BOSSS
        </motion.text>

        <motion.text
          x={centerX}
          fill="white"
          fontSize="8"
          y={centerY + 8}
          fontWeight="bold"
          textAnchor="middle"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, delay: 0.9 }}
        >
          Implementation
        </motion.text>

        {/* Connection lines from center to phases */}
        {phasePositions.map((phase, i) => {
          // Calculate start point (edge of central circle, r=35)
          const startX = centerX + 35 * Math.cos(phase.angle);
          const startY = centerY + 35 * Math.sin(phase.angle);

          // Calculate end point (edge of phase circle, r=28)
          // We subtract because we want the point on the side closer to the center
          const endX = phase.x - 28 * Math.cos(phase.angle);
          const endY = phase.y - 28 * Math.sin(phase.angle);

          return (
            <motion.line
              x2={endX}
              y2={endY}
              x1={startX}
              y1={startY}
              strokeWidth="2"
              key={`line-${i}`}
              stroke={phase.color}
              strokeLinecap="round"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 1, delay: 0.5 + i * 0.1 }}
            />
          );
        })}

        {/* Phase Nodes */}
        {phasePositions.map((phase, i) => {
          const IconComponent = phase.icon;
          return (
            <g key={i}>
              <motion.circle
                r="28"
                cx={phase.x}
                cy={phase.y}
                fill="white"
                strokeWidth="2"
                stroke={phase.color}
                initial={{ scale: 0 }}
                animate={{
                  scale: hoveredPhase === phase.label ? 1.1 : 1,
                }}
                transition={{
                  duration: 0.5,
                  delay: 0.7 + i * 0.1,
                }}
                style={{
                  filter:
                    'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.1))',
                  cursor: 'pointer',
                }}
                onMouseLeave={() => setHoveredPhase(null)}
                onMouseEnter={() => setHoveredPhase(phase.label)}
              />

              {/* Phase icon */}
              <motion.foreignObject
                width="16"
                height="16"
                x={phase.x - 8}
                y={phase.y - 12}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                style={{ pointerEvents: 'none' }}
                transition={{
                  duration: 0.3,
                  delay: 1.0 + i * 0.1,
                }}
              >
                <IconComponent
                  className="w-4 h-4"
                  style={{ color: phase.color }}
                />
              </motion.foreignObject>

              {/* Phase label */}
              <motion.text
                x={phase.x}
                fontSize="9"
                y={phase.y + 8}
                fontWeight="bold"
                fill={phase.color}
                textAnchor="middle"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                style={{ pointerEvents: 'none' }}
                transition={{
                  duration: 0.3,
                  delay: 1.2 + i * 0.1,
                }}
              >
                {phase.label}
              </motion.text>
            </g>
          );
        })}
      </svg>
    </DiagramContainer>
  );
}

export function ImplementationServicesPage() {
  const onNavigateToTop = useNavigateToTop();
  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: BG_COLOR }}
    >
      {/* Hero Section */}
      <section className="py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Column - Text Content */}
            <motion.div
              className="space-y-8"
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              initial={{ opacity: 0, x: -50 }}
            >
              <div className="space-y-6">
                <div
                  style={{ color: BOSSS_BLUE }}
                  className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-blue-50"
                >
                  <Rocket className="w-4 h-4 mr-2" />
                  BOSSS Implementation Services
                </div>

                <h1
                  style={{ color: TEXT_COLOR }}
                  className="text-4xl lg:text-5xl font-bold"
                >
                  Implementation Services – From Planning to
                  Go-Live
                </h1>

                <p
                  style={{ color: TEXT_COLOR }}
                  className="text-lg leading-relaxed"
                >
                  Our expert implementation team ensures a
                  smooth, successful deployment of your BOSSS
                  platform with proven methodologies and
                  comprehensive support throughout your journey.
                </p>
              </div>

              {/* Bullet Points with Icons */}
              <div className="space-y-4">
                {[
                  {
                    icon: Calendar,
                    text: 'Comprehensive project planning and timeline management',
                  },
                  {
                    icon: Settings,
                    text: 'Expert configuration tailored to your environment',
                  },
                  {
                    icon: Puzzle,
                    text: 'Seamless integration with existing systems and tools',
                  },
                  {
                    icon: GraduationCap,
                    text: 'Thorough training programs for your team',
                  },
                  {
                    icon: Rocket,
                    text: 'Smooth go-live support and ongoing optimization',
                  },
                ].map((item, index) => {
                  const IconComponent = item.icon;
                  return (
                    <motion.div
                      key={index}
                      animate={{ opacity: 1, x: 0 }}
                      initial={{ opacity: 0, x: -20 }}
                      className="flex items-center space-x-4"
                      transition={{
                        duration: 0.5,
                        delay: 0.2 + index * 0.1,
                      }}
                    >
                      <div
                        style={{ backgroundColor: BOSSS_BLUE }}
                        className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                      >
                        <IconComponent className="w-5 h-5 text-white" />
                      </div>

                      <span
                        className="text-base"
                        style={{ color: TEXT_COLOR }}
                      >
                        {item.text}
                      </span>
                    </motion.div>
                  );
                })}
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <motion.div
                  whileTap={{ scale: 0.95 }}
                  whileHover={{ scale: 1.05 }}
                >
                  <Button
                    style={{ backgroundColor: BOSSS_BLUE }}
                    className="text-white px-8 py-4 text-lg rounded-lg transition-all duration-200 hover:shadow-xl"
                    onClick={() => onNavigateToTop('/demo')}
                  >
                    Start Your Implementation
                  </Button>
                </motion.div>

                <motion.div
                  whileTap={{ scale: 0.95 }}
                  whileHover={{ scale: 1.05 }}
                >
                  <Button
                    variant="outline"
                    className="px-8 py-4 text-lg rounded-lg transition-all duration-200 hover:shadow-lg"
                    style={{
                      borderColor: BOSSS_BLUE,
                      color: BOSSS_BLUE,
                    }}
                    onClick={() => onNavigateToTop('/contact')}
                  >
                    Talk to an Expert
                  </Button>
                </motion.div>
              </div>
            </motion.div>

            {/* Right Column - Implementation Phases Diagram */}
            <motion.div
              animate={{ opacity: 1, x: 0 }}
              initial={{ opacity: 0, x: 50 }}
              className="relative flex justify-center"
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <ImplementationPhasesCircularDiagram />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Answer-first overview (SEO-82) */}
      <AnswerSection path="/services/implementation" />


      {/* Our Implementation Process */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-16"
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2
              style={{ color: TEXT_COLOR }}
              className="text-3xl lg:text-4xl font-bold mb-4"
            >
              Our Implementation Process
            </h2>

            <p
              className="text-xl"
              style={{ color: TEXT_COLOR, opacity: 0.8 }}
            >
              A proven methodology that ensures successful BOSSS
              deployments every time.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            {[
              {
                number: '01',
                title: 'Discovery & Planning',
                description:
                  'Assess your environment and create a detailed implementation roadmap.',
                icon: Calendar,
                color: '#10B981',
              },
              {
                number: '02',
                title: 'System Configuration',
                description:
                  'Configure BOSSS modules to match your specific requirements.',
                icon: Settings,
                color: '#F59E0B',
              },
              {
                number: '03',
                title: 'Integration Setup',
                description:
                  'Connect BOSSS with your existing systems and data sources.',
                icon: Puzzle,
                color: '#EF4444',
              },
              {
                number: '04',
                title: 'Training & Testing',
                description:
                  'Comprehensive training and thorough testing before go-live.',
                icon: GraduationCap,
                color: '#8B5CF6',
              },
              {
                number: '05',
                title: 'Launch & Support',
                description:
                  'Go-live support and ongoing optimization assistance.',
                icon: Rocket,
                color: BOSSS_BLUE,
              },
            ].map((step, index) => {
              const IconComponent = step.icon;
              return (
                <motion.div
                  key={index}
                  viewport={{ once: true }}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.6,
                    delay: index * 0.1,
                  }}
                >
                  <Card className="h-full bg-white rounded-xl shadow-lg border-0 hover:shadow-xl transition-all duration-300 group text-center">
                    <CardHeader className="pb-4">
                      <div
                        className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300"
                        style={{
                          backgroundColor: `${step.color}15`,
                        }}
                      >
                        <IconComponent
                          className="w-8 h-8"
                          style={{ color: step.color }}
                        />
                      </div>

                      <div
                        style={{ backgroundColor: BOSSS_BLUE }}
                        className="w-12 h-12 mx-auto rounded-full flex items-center justify-center mb-4"
                      >
                        <span className="text-sm font-bold text-white">
                          {step.number}
                        </span>
                      </div>

                      <CardTitle
                        className="text-lg"
                        style={{ color: TEXT_COLOR }}
                      >
                        {step.title}
                      </CardTitle>
                    </CardHeader>

                    <CardContent className="text-center">
                      <p
                        className="leading-relaxed text-sm"
                        style={{
                          color: TEXT_COLOR,
                          opacity: 0.8,
                        }}
                      >
                        {step.description}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose BOSSS */}
      <section
        className="py-20"
        style={{ backgroundColor: BG_COLOR }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-16"
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2
              style={{ color: TEXT_COLOR }}
              className="text-3xl lg:text-4xl font-bold mb-4"
            >
              Why Choose BOSSS Implementation Services?
            </h2>

            <p
              className="text-xl"
              style={{ color: TEXT_COLOR, opacity: 0.8 }}
            >
              Proven expertise, accelerated time-to-value, and
              ongoing success.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Clock,
                title: 'Faster Time-to-Value',
                description:
                  'Our proven methodology reduces implementation time by 50% compared to self-deployment, getting you operational quickly with minimal disruption.',
              },
              {
                icon: Shield,
                title: 'Risk-Free Implementation',
                description:
                  'Minimize deployment risks with our experienced implementation team and proven methodology designed for enterprise environments.',
              },
              {
                icon: HeadphonesIcon,
                title: 'Ongoing Support',
                description:
                  'Comprehensive post-implementation support ensures your BOSSS deployment continues to deliver value and adapt to your evolving needs.',
              },
            ].map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <motion.div
                  key={index}
                  viewport={{ once: true }}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.6,
                    delay: index * 0.1,
                  }}
                >
                  <Card className="h-full bg-white rounded-xl shadow-lg border-0 hover:shadow-xl transition-all duration-300 group text-center p-8">
                    <CardContent className="space-y-6">
                      <div
                        className="w-20 h-20 mx-auto rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300"
                        style={{
                          backgroundColor: `${BOSSS_BLUE}15`,
                        }}
                      >
                        <IconComponent
                          className="w-10 h-10"
                          style={{ color: BOSSS_BLUE }}
                        />
                      </div>

                      <h3
                        style={{ color: TEXT_COLOR }}
                        className="text-2xl font-bold"
                      >
                        {benefit.title}
                      </h3>

                      <p
                        className="leading-relaxed"
                        style={{
                          color: TEXT_COLOR,
                          opacity: 0.8,
                        }}
                      >
                        {benefit.description}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section
        className="py-20"
        style={{
          background: `linear-gradient(135deg, ${BG_COLOR} 0%, #e0f2fe 50%, #b3e5fc 100%)`,
        }}
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2
              style={{ color: TEXT_COLOR }}
              className="text-3xl lg:text-4xl font-bold mb-6"
            >
              Ready to Begin Your BOSSS Implementation?
            </h2>

            <p
              className="text-xl mb-8 max-w-2xl mx-auto"
              style={{ color: TEXT_COLOR, opacity: 0.8 }}
            >
              Let our expert implementation team guide you
              through a successful BOSSS deployment. Contact us
              today to discuss your timeline and requirements.
            </p>

            <motion.div
              whileTap={{ scale: 0.95 }}
              whileHover={{ scale: 1.05 }}
            >
              <Button
                style={{ backgroundColor: BOSSS_BLUE }}
                className="text-white px-8 py-4 text-lg rounded-lg transition-all duration-200 hover:shadow-xl font-semibold"
                onClick={() => onNavigateToTop('/contact')}
              >
                Start Your Implementation Journey
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
